import { BILLING_REGION_NONE } from '@/lib/billing-region';
import { orDash } from '@/lib/format';
import type { NewBusinessGateBasis } from '@/lib/types';

/*
 * THE COMMISSION VOCABULARY — ONE COPY.
 *
 * Three pages and three modals render commissions. Every sentence about how a
 * payout was arrived at, every rule for reading a percentage, a gate or a
 * clawback, and every test this app performs on a scheme lives here and nowhere
 * else.
 *
 * ⚠️ **Two copies would agree the day they were written and drift on the first
 * correction — and what drifts here is wording about somebody's PAY.** That is
 * the same argument `src/lib/inclusions.ts` makes for the singular/plural pair
 * and `src/lib/company-projects.ts` makes for how a list of projects reads in a
 * dense cell, one rung more consequential: an inclusion prints on a customer's
 * document, and a commission sentence describes what a colleague is owed.
 *
 * ## ⛔ NOTHING IN THIS FILE FETCHES OR WRITES. IT DECIDES.
 *
 * No `api` import, no React, no hooks, no state. The consumers own their own
 * requests and their own draft state; this module answers *"given what the
 * server sent, what should the screen say and what tone should it say it in?"*
 *
 * ⛔ **AND NO HOOK MAY BE ADDED**, which is a standing ban rather than an
 * observation about today. The consumers legitimately need DIFFERENT fetches: a
 * scheme form needs the model and gate-shape masters plus the billing-region
 * names; a run page needs its statements and nothing else; a statement page
 * needs its clawback rows. A hook here would force one fetching shape on all of
 * them and make the cheap surface pay for the expensive one — which is the
 * reasoning `company-projects.ts` records for its own identical ban, and it is
 * the reason a "convenience" hook here would be a regression rather than a
 * tidy-up.
 *
 * ## ⛔ THE ONE IMPORT THAT LOOKS LIKE A VIOLATION AND IS NOT
 *
 * `@/lib/billing-region` transitively imports `@/lib/api`, because it also
 * exports `useBillingRegionLabels`. **This file still fetches nothing** — it
 * re-exports one slug constant — and the alternative was worse: a second
 * literal `'none'` in `src/`, which is precisely the duplicated-vocabulary
 * failure this module exists to prevent. The trade was weighed rather than
 * overlooked. ⛔ **Do not "fix" it by declaring a local copy of the slug**, and
 * do not read the transitive edge as permission to import `api` here directly.
 *
 * ## ⛔ THERE IS DELIBERATELY NO LABEL MAP FOR THE BILLING REGIONS HERE
 *
 * The region NAMES are admin-editable columns on `f_billing_region_defaults`,
 * so they are FETCHED. `src/lib/billing-region.ts` states the rule at length and
 * this module obeys it rather than restating it: a literal
 * `{united_kingdom: 'United Kingdom', …}` anywhere in `src/` is a second copy of
 * wording the server already owns — correct on the day it is written, silently
 * wrong after the first rename, with nothing failing. **A caller resolves names
 * through `useBillingRegionLabels()`**, which is a hook and therefore lives
 * there and not here.
 *
 * ## ⚠️ THE SHAPES BELOW ARE STRUCTURAL, AND THAT IS ON PURPOSE
 *
 * Every parameter type in this file is declared locally and **deliberately not
 * imported from `types.ts`**, exactly as `CompanyProject` is in
 * `company-projects.ts` and for the same two reasons: a wire type named here
 * would make this module wrong the moment the response shape moved, in a file
 * that has no business knowing about the wire at all; and each function names
 * only the fields it actually reads, so a rule like *"word the gate from the
 * factor, never from the flag"* is enforced by `tsc` refusing to hand the
 * function the flag — rather than by a sentence somebody has to remember.
 *
 * ## THE ARITHMETIC THESE FUNCTIONS DESCRIBE
 *
 * Not performed here — the run computes every figure server-side and freezes it
 * onto the statement. It is written out so the wording below can be checked
 * against it. `migrations/204_create_commission_runs.sql` is the contract.
 *
 * ```
 *   personal commission     = personal rate  x  personal revenue    never gated
 *   regional pool           = regional rate  x  regional revenue    gated
 *   effective regional pool = regional pool  x  gate factor
 *
 *   gross payable  top-up   max( personal commission, effective pool )
 *                  offset   max( personal commission,
 *                                effective pool minus personal commission )
 *
 *   net payable             = max( 0, gross payable minus clawback )
 * ```
 *
 * ⚠️ **THE FLOOR AT THE PERSONAL COMMISSION IS NOT A SPECIAL CASE.** It falls
 * out of the max once the gate factor is zero, on both models. The migration
 * records it as INFERRED from two owner decisions read together rather than as
 * one the owner stated, which is why it is worth knowing that it is derived and
 * can be overturned deliberately.
 */

/* -------------------------------------------------------------------------
 * The sentences. Each says where it may be rendered AND where it may not.
 * --------------------------------------------------------------------- */

/**
 * WHO CAN SEE THESE FIGURES — owner decision 24, and the only sentence in this
 * app about a module that discloses PAY.
 *
 * ⚠️ **THIS IS A FACT ABOUT THE DATA THAT THE READER CANNOT OTHERWISE SEE, WHICH
 * IS THE DOCUMENTED EXCEPTION TO THE NO-HELPER-TEXT PROHIBITION — NOT A CARVE-OUT
 * FROM IT.** `CLAUDE.md` carries a standing ⛔ ban on helper text: no `hint`, no
 * `description`, no explanatory sentence under a control. This survives under the
 * same exception as `CONVERSION_APPROXIMATE_NOTE`, the kanban board's
 * *"Indicative — mixed GBP, USD"* and a `Combobox`'s truncation footnote — it
 * **qualifies a figure rather than instructing anybody**, and nothing on screen
 * distinguishes a commission module every colleague can open from one only two
 * administrator tiers can. **A helper-text sweep must not delete it.**
 *
 * **Before removing it, ask what wrong conclusion it prevents.** The answer is
 * not "none": an administrator looking at a colleague's statement has no way to
 * tell whether that colleague is also looking at it, and the natural assumption —
 * that a lead can see their own — is false. Somebody acting on that assumption
 * discusses a figure as though it were already known to the person it names.
 *
 * ⚠️ **It states a DECISION, so it does not rot the way an asserted absence
 * does.** `CLAUDE.md` forbids sentences of the form *"there is no way to…"*
 * because they are falsified the moment somebody builds the thing and the builder
 * never re-reads the sentence. This is the other kind: owner decision 24 is a
 * ruling somebody would have to overturn, and **this constant is the one place
 * that changes when they do.**
 *
 * ⛔ **WHERE IT MAY NOT GO.** Not on any surface outside the commissions module,
 * and ⛔ **never as a `denialReason` or a permission refusal** — those answer
 * *"why can't I do this?"* for the reader in front of them, where this answers
 * *"who else is looking at this?"* about everybody else. Routing it to a refusal
 * would tell an administrator who has just been refused something that they are
 * an administrator.
 */
export const COMMISSION_VISIBILITY_NOTE = {
  /** Under a heading. */
  short: 'Visible to administrators only.',
  /** The same fact in full, for a `title`. */
  detail:
    'Commission schemes, assignments and statements are visible to administrators only. ' +
    'No sales lead sees a commission figure in SEED EMERGE — not their own and not a ' +
    'colleague’s — so nothing a person reads here has been shown to the lead it names.',
} as const;

/**
 * WHY AN APPROVED STATEMENT DOES NOT MOVE — owner decision 15.
 *
 * Every term, date, rate and figure is COPIED onto the statement when the run
 * computes, and nothing on it is resolved by a join at read time. So reassigning
 * an account, editing a scheme's rate or retiring a project afterwards changes
 * nothing about what somebody was paid on.
 *
 * ⚠️ **THE FREEZE IS AT COMPUTE, NOT AT APPROVAL, AND THAT IS THE SUBTLE HALF.**
 * Approval makes a run UNRECOMPUTABLE; the figures were already frozen when they
 * were written. The window between the two — year end and approval — is exactly
 * when somebody is reviewing the draft, and a recompute in it legitimately moves
 * the numbers. **This sentence is about a statement, never about a run**, which
 * is why it says *"when this run computed"* rather than *"when this run was
 * approved"*.
 *
 * ⛔ **APPROVED RUNS ONLY. IT MUST NOT RENDER ON A DRAFT**, where it would be a
 * false promise about figures that will move on the next recompute —
 * {@link LIVE_ATTRIBUTION_NOTE} is the draft's sentence and the two are mutually
 * exclusive.
 *
 * ⛔ **GATE IT ON THE RUN'S `approvedAt` OR ITS STATUS FLAG — NEVER ON THE
 * PRESENCE OF THE APPROVER.** `approved_by_id` is `ON DELETE SET NULL`, so a
 * departed colleague would make an approved run read as unapproved and put the
 * draft's warning on a frozen year. The migration states this rule at the column
 * and the credit-note module states it at its own; it is not a client
 * convention. ⚠️ And read `is_approved` rather than testing `is_void` — the two
 * are **not complements**, because a draft is neither.
 */
export const FROZEN_STATEMENT_NOTE = {
  /** Beside the figures on an approved statement. */
  short: 'Frozen when this run computed.',
  /** The same fact in full, for a `title`. */
  detail:
    'Every figure on this statement — the revenue, the rates, the targets, the exchange ' +
    'rates and who each amount was credited to — was copied onto it when the run computed, ' +
    'and none of it is looked up again. Reassigning an account or editing a scheme afterwards ' +
    'does not change what this person was paid on.',
} as const;

/**
 * THE INVERSE, FOR A DRAFT — and it is the one of the pair that names a hazard.
 *
 * Revenue is credited through the **RESOLVED account lead, read LIVE**:
 * `COALESCE(document.account_lead_id, company.account_lead_id)`. So until the run
 * is approved, **moving one company to another lead rewrites a closed year's
 * figures, with nothing erroring.** Both numbers stay internally consistent,
 * neither screen looks wrong, and the only trace is that a draft says something
 * different today than it said yesterday.
 *
 * ⚠️ **That is not a defect to be fixed here.** It is the owner's 2026-08-30
 * decision replacing frozen credit columns with live resolution, taken because a
 * `recognised_on`-style stamp could never be backfilled onto records that reached
 * their terminal state before the column existed. The mitigation is approval, and
 * that is what this sentence points at.
 *
 * ⛔ **DRAFT RUNS ONLY. IT MUST NOT RENDER ON AN APPROVED ONE**, where it would
 * describe a risk that approval has already closed and would undermine the one
 * sentence a reader needs to trust. Gate it on the same flag as
 * {@link FROZEN_STATEMENT_NOTE}, and with the same ⛔ about the approver's row.
 *
 * ⚠️ **It states a FACT and not an instruction**, following the rule
 * `excludedByCurrencyNote` keeps in `targets.ts`. It does not tell anybody to
 * approve the run: whether a year is ready to be frozen is a judgement the reader
 * is making, and a sentence nudging them towards it would be advice attached to a
 * control, which is the shape the no-helper-text rule refuses.
 */
export const LIVE_ATTRIBUTION_NOTE = {
  /** Beside the figures on a draft statement. */
  short: 'Draft — attribution is read live.',
  /** The same fact in full, for a `title`. */
  detail:
    'While this run is a draft, revenue is credited to whoever leads each account TODAY, ' +
    'not to whoever led it when the work was billed. So moving a company to a different ' +
    'account lead changes these figures — including for a year that has already closed. ' +
    'Approving the run freezes them.',
} as const;

/**
 * ⛔ CREDITING AN INVOICE DOES NOT REDUCE THE REVENUE FIGURE.
 *
 * A credit note is a **separate document with its own total**. It never touches
 * the invoice it credits, which keeps its status, its amount and its place in the
 * year — so a **FULLY CREDITED INVOICE STILL COUNTS AT 100%** towards both the
 * personal and the regional figure. The clawback is the only thing that makes the
 * number honest, and the subtraction is explicit.
 *
 * ⚠️ **THE NATURAL ASSUMPTION IS THE OPPOSITE AND NOTHING ON ANY SCREEN WOULD SAY
 * SO.** A reader who assumes the revenue drops reads the clawback as a second
 * deduction on money already removed, and concludes the statement double-counts —
 * or, worse, checks a revenue figure against an invoice list, finds them agreeing,
 * and takes that as proof the credits were handled. Both readings are confident,
 * both are wrong, and neither produces an error.
 *
 * ⚠️ **ONLY AN APPROVED CREDIT NOTE COUNTS**, never a draft: the run reads
 * `is_approved` on the credit-note status, and `is_approved` and `is_void` are
 * **not complements** there either, so the test is the positive one.
 *
 * ⚠️ **A CREDIT CAN LAND ON THE WRONG PERSON AND THIS MODULE CANNOT FIX IT.** An
 * invoice may name its own account lead, overriding the company; a credit note
 * carries a company and no lead of its own, so it takes whoever leads that company
 * TODAY. An invoice one lead was credited for, on an account since moved, reduces
 * the NEW lead. The clawback row's `source_user_id` is what makes that visible —
 * **render it where a clawback names somebody other than the statement's own
 * person**, rather than letting the figure stand unexplained.
 *
 * ⛔ **WHERE IT MAY NOT GO: an invoice page or a credit-note page.** There it
 * would read as a claim about that document's own balance — which it is not — and
 * on a credit note it would look like a denial that the credit had any effect.
 * This is a sentence about how the COMMISSION arithmetic treats a credit, so it
 * belongs beside a commission revenue figure or at the head of a clawback block.
 */
export const CREDIT_DOES_NOT_REDUCE_REVENUE_NOTE = {
  /** At the head of a clawback block, or beside a revenue figure. */
  short: 'Credits are clawed back, not netted off the revenue.',
  /** The same fact in full, for a `title`. */
  detail:
    'A credit note is a separate document and never changes the invoice it credits, so a ' +
    'fully credited invoice still counts in full towards the revenue above. The clawback ' +
    'below is what takes the commission back — it is the only subtraction, not a second one.',
} as const;

/**
 * ⚠️ EDITING A SCHEME'S TERMS CHANGES WHAT EVERY ASSIGNMENT ON IT THAT HAS NOT
 * YET BEEN RUN WILL COMPUTE.
 *
 * A scheme is a TEMPLATE and its terms are **not** copied onto an assignment at
 * creation — deliberately, because copying silently makes the template a one-time
 * seed and the next person to edit a rate would find it changed nothing. Approval
 * freezes a RUN, which protects every year already paid; **it does not protect a
 * year that has been configured and not yet run.**
 *
 * ⚠️ **That is usually what a template is FOR**, which is exactly why it has to be
 * said out loud: the opposite reading is just as reasonable and would be a
 * different product, so a reader who assumes terms are pinned at assignment will
 * edit a rate believing it applies to next year only.
 *
 * ⛔ **THE SERVER WARNS AND NAMES THE LIVE ASSIGNMENTS RATHER THAN REFUSING.**
 * Render the server's own list verbatim — the names are the whole actionable
 * content — and render this sentence BESIDE it, never INSTEAD of it. This is the
 * standing fact; the server's list is the specific one, and a house sentence in
 * place of it is the `MAIL_SEND_FAILED` mistake this repo already records.
 *
 * ⛔ **WHERE IT MAY NOT GO: an ASSIGNMENT form.** There it describes a
 * consequence of editing something else, on a screen that cannot cause it, and it
 * would read as a warning about the save in front of the reader. It belongs on the
 * scheme form and in the confirmation that precedes a scheme save.
 */
export const TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE = {
  /** On the scheme form, and in the confirmation before a scheme save. */
  short: 'Changing these terms changes every assignment not yet run.',
  /** The same fact in full, for a `title`. */
  detail:
    'A scheme is a template and its terms are read when a run computes, not copied when an ' +
    'assignment is created. So editing a rate, a gate or the clawback window changes what ' +
    'every assignment on this scheme will compute — including assignments for years already ' +
    'configured. Years that have been run and approved are frozen and are not affected.',
} as const;

/**
 * WHAT A REVENUE FIGURE LEFT OUT, worded once — or `null` when there is nothing
 * to say.
 *
 * A document with **no exchange rate stored for its own date** is left out of the
 * revenue above and counted here, never converted at par. So a commission figure
 * can be **SMALLER than the revenue it represents**, and nothing else on the
 * statement would say so.
 *
 * ⛔ **IT MUST NAME A MISSING EXCHANGE RATE SOMEBODY CAN BACKFILL, NEVER "IN
 * ANOTHER CURRENCY".** This repo shipped that exact wording wrong once, on
 * `excludedByCurrency`, and it was live on production for a day: *"in another
 * currency"* names a POLICY the reader cannot change and has no reason to
 * question, where the truth is *"we could not price these"* — a gap in a rate
 * table that a backfill closes. **The two send a reader to opposite places**, and
 * the wrong one was wrong in the reassuring direction, which is the direction
 * nobody reports.
 *
 * ⛔ **AND IT CARRIES NO INSTRUCTION, WHICH IS A SECOND LESSON FROM THE SAME
 * INCIDENT.** The superseded sentence ended *"Switch the currency above to see
 * them"*, which stopped working when the field changed meaning — **a dead
 * instruction is worse than none, because it reads as a way out and spends the
 * reader's trust when it fails.** Here the equivalent temptation is *"recompute
 * the run"*, and it would be dead on every APPROVED statement, which is most of
 * the ones anybody reads. The sentence states the fact; what to do about it is a
 * judgement the reader is already making.
 *
 * ⚠️ **`0` PRINTS NOTHING, AND `0` IS THE HEALTHY ANSWER PRODUCTION GIVES
 * TODAY** — which is exactly why this must be rendered at all. A figure that is
 * only ever right is a figure nobody builds a reader for, and the day a rate gap
 * appears a statement would otherwise under-pay somebody in silence. An
 * "0 excluded" line on every statement is the noise that teaches a reader to stop
 * looking at the small print, at which point the one time it says 2 is the one
 * time it is not read.
 *
 * ⚠️ **THE COLUMN IS `NOT NULL`, so there is no *did-not-look* state here** —
 * unlike the nullable count the revenue tab carries for an older server. `null` is
 * still accepted and still returns nothing, because a client newer than its API
 * is a state this app is built for and inventing a sentence from an absence would
 * be a claim nobody made.
 *
 * ⛔ **THERE ARE TWO COUNTS ON A STATEMENT AND THEY MUST NEVER BE ADDED
 * TOGETHER.** `personalExcludedDocuments` and `regionalExcludedDocuments` are
 * taken over **overlapping populations** — a lead's own documents are part of
 * their region's — so a document may be counted in both, and one summed figure
 * reports a larger gap than the statement has. Call this once per figure, beside
 * the figure it describes.
 *
 * ⚠️ **IT RETURNS ONE SENTENCE RATHER THAN THE `{short, detail}` PAIR THE
 * DASHBOARD NOTES USE**, and that is a decision rather than an inconsistency: a
 * statement is a PAGE, where the tiles that pair was written for are small. A
 * split here would produce a `short` nobody needs and a `title` nobody hovers.
 *
 * ⛔ **DO NOT IMPORT `excludedByCurrencyNote` FROM `targets.ts` INSTEAD.** The
 * underlying gap is the same and the fact being reported is not: there, a
 * dashboard total is short; here, **a person's pay is computed on less revenue
 * than they earned**. One sentence cannot carry both consequences, and the one
 * that tried would have to drop the half that matters on a statement.
 */
export function EXCLUDED_DOCUMENTS_NOTE(count: number | null): string | null {
  if (count === null || !Number.isFinite(count) || count < 1) return null;
  /*
   * Both forms written out inline, exactly as `excludedByCurrencyNote` does.
   * `format.ts`'s `pluralise` would default `documents` correctly — which is
   * precisely why the habit survives long enough to reach the first irregular
   * noun, the failure `src/lib/inclusions.ts` records (`1 campuses`, on a
   * document a customer receives).
   */
  const noun = count === 1 ? 'document' : 'documents';
  const dates = count === 1 ? 'its own date' : 'their own dates';
  const them = count === 1 ? 'it' : 'them';
  return (
    `${count} ${noun} had no exchange rate stored for ${dates}, so this figure leaves ` +
    `${them} out and the commission is computed on less revenue than was earned. ` +
    `The rate is missing, not the revenue.`
  );
}

/* -------------------------------------------------------------------------
 * The billing bands a scheme measures.
 * --------------------------------------------------------------------- */

/**
 * The companies nobody has banded — those with no country of record.
 *
 * ⚠️ **THIS IS A RE-EXPORT, NOT A SECOND COPY.** The slug lives in
 * `src/lib/billing-region.ts`, which owns the band vocabulary and its order, and
 * a second literal `'none'` in `src/` would be the duplicated-vocabulary failure
 * this module exists to prevent. It is re-exported so a commissions surface has
 * one import rather than two, and so {@link isUnbandedRegion} can be the named
 * test rather than a `=== 'none'` written out at six call sites.
 *
 * 🔴 **`'none'` IS A REAL BAND AND IS NEVER THE SAME AS AN EMPTY BAND LIST — and
 * the two are one keystroke apart in every form that edits a scheme.**
 *
 * ```
 * billingRegions: ['none']   a scheme measuring the UNBANDED COMPANIES
 * billingRegions: []         a PERSONAL-ONLY scheme, measuring no region at all
 * ```
 *
 * ⛔ **Both ways round, because both mistakes are silent.** Reading `['none']` as
 * *"no regions chosen"* turns a real commitment into a personal-only scheme and
 * the regional pool quietly stops being computed. Reading `[]` as *"the unbanded
 * band"* invents a commitment nobody made and starts paying a pool against a
 * population the scheme was never about. Neither errors, both produce a
 * well-formed money figure, and the difference between them is somebody's bonus.
 *
 * ⚠️ **NOT the same sentinel as the revenue tab's `NO_REGION_KEY`, which is
 * `(none)` with load-bearing parentheses.** That one shares a URL slot with real
 * ENUM members and needs a spelling no member could take. This is a real value on
 * a real column, spelled as the server stores it.
 */
export { BILLING_REGION_NONE };

/**
 * Whether a band slug names the unbanded companies.
 *
 * ⚠️ **It takes a `string` rather than a narrow union deliberately.** The band
 * arrives on a scheme as the server spells it, and a union imported from
 * `types.ts` would tie this module to a wire type that another surface may widen
 * — in a file whose whole argument is that it does not know about the wire. The
 * test is exact string equality against the one stored spelling, which is the
 * strongest thing available and does not weaken with the parameter type.
 *
 * ⛔ **Exact equality, never `includes()`, never a case fold, never a prefix.**
 * That rule is not decoration here: this codebase has been bitten four times by a
 * client deciding behaviour from a loosely-matched string — `includes('paid')`
 * matching `"Unpaid"`, `includes('accept')` matching `"Not Accepted"`, a product
 * rename switching campaign inclusions off, and a fuzzy inclusion name. This slug
 * is a real contract rather than admin-editable text, so exact membership is both
 * cheap and correct.
 */
export function isUnbandedRegion(slug: string): boolean {
  return slug === BILLING_REGION_NONE;
}

/** What {@link schemeMeasuresRegions} needs of a scheme. */
export interface SchemeBands {
  /**
   * The bands this scheme measures, as the server spells them.
   *
   * ⚠️ **Read this, never a statement's `billingRegionsSnapshot`.** That column is
   * an AUDIT COPY — a comma-joined string frozen onto the statement so a reader
   * years later can see what the run counted — and the migration says in terms
   * that it is never parsed and never matched on. Anything reading it as data is
   * matching on free text by another name.
   */
  billingRegions: readonly string[];
}

/**
 * Whether this scheme pays a regional pool at all — **the named test, so no call
 * site writes `billingRegions.length > 0` itself.**
 *
 * ⚠️ **A bare length check at a call site is not wrong today and is the thing
 * that goes wrong later.** It reads as an emptiness check on an array, so the day
 * somebody decides the band set may arrive `null` from an older server, or that a
 * scheme with only retired bands measures nothing, the rule has to be found at
 * every site rather than changed in one. That is the same argument
 * `metricIsMoney` makes in `targets.ts` for a test that is currently one `Set`
 * lookup.
 *
 * ⛔ **An EMPTY list is a PERSONAL-ONLY scheme — a real, chosen configuration and
 * not a half-filled form.** See {@link BILLING_REGION_NONE} for the pair of
 * silent misreadings this and the unbanded band produce in each other's
 * direction. ⛔ **Do not word an empty list as "no regions selected"** anywhere a
 * reader can see: that describes a form somebody has not finished, where the
 * scheme is finished and deliberate.
 */
export function schemeMeasuresRegions(scheme: SchemeBands): boolean {
  return scheme.billingRegions.length > 0;
}

/* -------------------------------------------------------------------------
 * Percentages.
 * --------------------------------------------------------------------- */

/**
 * A stored rate or gate as display text — `"2.0000"` reads `2%`.
 *
 * ```
 * '2.0000'     ->  '2%'
 * '1.5000'     ->  '1.5%'
 * '0.1250'     ->  '0.125%'
 * '0.0000'     ->  '0%'          <- a real rate of zero
 * '100.0000'   ->  '100%'
 * null         ->  '—'           <- no figure at all
 * ```
 *
 * ⛔ **`null` AND `'0.0000'` ARE DIFFERENT FACTS AND MUST NEVER BE FOLDED.** A
 * zero is a rate somebody set — a scheme that pays no regional percentage is a
 * real configuration — where `null` is *there is no figure here*. A `?? 0`
 * upstream turns the second into the first and prints a confident `0%` over a
 * field nobody filled in. The em dash comes from `orDash(null)` rather than a
 * literal, so this file does not own a second copy of that glyph.
 *
 * ⚠️ **THE STORED CONVENTION IS `2.0000` FOR TWO PER CENT, NEVER `0.02`**, and
 * every column carries the `_percent` suffix so no call site has to remember
 * which. A rate read in the other convention is out by a factor of a hundred,
 * produces a well-formed money figure, and **there is no plausibility check on a
 * commission that catches either direction** — the run would pay 1,160,000
 * instead of 11,600, or 116 instead of 11,600.
 *
 * ## ⚠️ WHICH STRINGS MAY BE PARSED, AND WHICH MAY NOT
 *
 * ⛔ **NEVER `Number()` A MONEY STRING.** Money arrives as an exact decimal and
 * every figure on a statement is one; a float round-trip is how a penny moves on
 * a figure somebody is paid against. {@link payoutBreakdown} therefore passes
 * money through **untouched**, and the caller formats it.
 *
 * ⚠️ **A PERCENTAGE MAY BE PARSED FOR DISPLAY ONLY** — and this function still
 * does not, because it does not need to. Trailing zeros are removed by string
 * surgery, which is exact, needs no finiteness guard and cannot reformat
 * `0.125` into `0.13`. {@link attainmentTone} is the one place a percentage IS
 * parsed, and it says at the call why that is safe there and would not be here.
 *
 * ⚠️ **Anything that is not a plain decimal is returned VERBATIM, with no `%`
 * appended.** `formatDecimalString` takes the same approach for the same reason:
 * an unexpected value should be legible rather than invisible, and stapling a
 * percent sign to it would dress a defect up as a rate.
 */
export function formatPercent(value: string | null): string {
  const text = value?.trim() ?? '';
  /*
   * A blank string folds in with `null`. Both mean *there is no figure*, and
   * letting `''` through would render a lone `%` — a unit with nothing in front
   * of it, which reads as a figure that failed rather than one nobody has.
   */
  if (text === '') return orDash(null);

  if (!/^[+-]?\d+(\.\d+)?$/.test(text)) return text;

  /*
   * The `includes('.')` guard is load-bearing and looks redundant: without it,
   * `'100'` has its trailing zeros stripped to `'1'` — a hundred per cent
   * rendered as one, on a gate threshold, silently.
   */
  const trimmed = text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
  return `${trimmed}%`;
}

/* -------------------------------------------------------------------------
 * Attainment, and the gate.
 * --------------------------------------------------------------------- */

/**
 * How an attainment reads against its own threshold.
 *
 * ⛔ **`'unknown'` IS NOT A DEGRADED `'short'`.** *No regional commitment* and
 * *missed the target* are different facts about a person's year, and painting the
 * first in the second's tone tells an administrator a colleague fell short of
 * something nobody ever asked of them. `regional_target` is nullable precisely so
 * a scheme can carry no regional commitment, so this is the ordinary state of
 * every personal-only scheme rather than an edge.
 */
export type AttainmentTone = 'met' | 'near' | 'short' | 'unknown';

/**
 * How close an attainment has to come before it reads as `'near'` rather than
 * `'short'`.
 *
 * ⚠️ **A DISPLAY BAND AND NOTHING ELSE. IT DECIDES NO MONEY.** The gate factor
 * the server computed is what opens the pool and what the payout is built from;
 * this picks a tone for a reader scanning a list. Moving it changes which rows
 * look close and changes no figure anywhere. ⛔ **Do not reach for it in any
 * arithmetic**, and do not read a `'near'` as a claim that a sliding gate paid
 * something — {@link gateSummary} is what answers that, from the factor.
 */
export const ATTAINMENT_NEAR_RATIO = 0.9;

/**
 * The tone for one attainment against one threshold.
 *
 * Both arguments are PERCENTAGES in the stored convention — `"87.5000"` against a
 * threshold of `"80.0000"` is met.
 *
 * ⚠️ **THIS IS THE ONE PLACE A PERCENTAGE IS PARSED, AND IT IS SAFE HERE FOR A
 * REASON THAT DOES NOT TRANSFER.** The output is a TONE — four words, one of
 * which gets picked — so a float comparison landing a hair the wrong side of a
 * threshold at 99.999999% costs a colour, not a penny. ⛔ **The same latitude does
 * not reach money**: see {@link formatPercent} for the rule and
 * {@link payoutBreakdown} for what it means in practice.
 *
 * ⚠️ **A THRESHOLD OF ZERO IS MET BY EVERYTHING, INCLUDING ZERO ATTAINMENT.** The
 * gate columns permit `0`, and a gate at zero is a gate that is already open —
 * so the division that would follow is both undefined and unnecessary. It is
 * handled first, rather than left to produce an `Infinity` that happens to
 * compare correctly.
 *
 * ⚠️ **An unparseable figure is `'unknown'`, never `'short'`.** Same rule as the
 * `null` above it and for the same reason: the tone must not manufacture a
 * judgement out of a value nobody could read.
 */
export function attainmentTone(
  attainmentPercent: string | null,
  gatePercent: string,
): AttainmentTone {
  const attainedText = attainmentPercent?.trim() ?? '';
  if (attainedText === '') return 'unknown';

  const attained = Number(attainedText);
  const gate = Number(gatePercent.trim());
  if (!Number.isFinite(attained) || !Number.isFinite(gate)) return 'unknown';

  if (gate <= 0) return 'met';
  if (attained >= gate) return 'met';
  return attained >= gate * ATTAINMENT_NEAR_RATIO ? 'near' : 'short';
}

/**
 * The gate factor as a percentage, clamped so the words stay true — or `null`
 * when it cannot be read.
 *
 * ⛔ **THE CLAMP IS THE POINT AND IT IS NOT COSMETIC ROUNDING.** The factor is
 * `DECIMAL(9,8)`, so a partially-open gate can sit at `0.00100000` or
 * `0.99900000`. Rounded honestly those read `0%` and `100%` — **a sentence
 * saying the gate is shut beside a pool that paid something, and a sentence
 * saying it is fully open beside a pool that was cut.** Clamping a genuine
 * partial into `1%`–`99%` keeps the sentence right about **which of the three
 * states it is**, which is the thing a reader is actually taking from it.
 */
function gateOpenPercent(gateFactor: string): string | null {
  const factor = Number(gateFactor.trim());
  if (!Number.isFinite(factor)) return null;
  if (factor >= 1) return '100%';
  if (factor <= 0) return '0%';
  return `${Math.min(99, Math.max(1, Math.round(factor * 100)))}%`;
}

/** What {@link gateSummary} needs, and deliberately nothing more. */
export interface StatementGate {
  /**
   * `DECIMAL(9,8)` as an exact string: `1` fully open, `0` shut, anything
   * between it a sliding gate part-way.
   */
  gateFactor: string;
}

/**
 * Whether and **how far** the gate opened, in a short phrase.
 *
 * ```
 * '1.00000000'   ->  'Gate fully open'
 * '0.43000000'   ->  'Gate 43% open'
 * '0.00000000'   ->  'Gate closed — the regional pool pays nothing'
 * ```
 *
 * ⛔ **IT TAKES THE FACTOR AND NOTHING ELSE, AND THAT IS STRUCTURAL RATHER THAN
 * MINIMAL.** The statement also carries `gateMet` and `isSlidingGate`, and
 * **neither can word a partial**:
 *
 * - `gateMet` answers *did the pool open fully*, so on a sliding scheme it is
 *   `false` for every gate between shut and fully open. Wording from it prints
 *   *"gate not met"* over a pool that really did pay — the single most misleading
 *   sentence available on this page, because the figure beside it contradicts it.
 * - `isSlidingGate` describes the scheme's SHAPE, not this year's outcome. A
 *   sliding scheme whose gate happened to open fully is not *"sliding"*, it is
 *   open.
 *
 * Because the parameter names only the factor, `tsc` refuses to hand this
 * function either flag — **a guard someone can forget is not a guard**, which is
 * the rule `DealFormModal` keeps by leaving `projectId` off `FormValues`
 * entirely.
 *
 * ⚠️ **A CLIFF CAN ONLY PRODUCE 0 OR 1 BY CONSTRUCTION, so it never reaches the
 * partial branch — and if it ever does, the factor is still the honest
 * answer.** Rounding a fractional cliff to the shape its flag claims would hide
 * a real disagreement between the data and the scheme, which is a thing somebody
 * needs to see rather than a thing to tidy.
 *
 * ⚠️ **An unreadable factor says so** rather than guessing at either end: a gate
 * silently reported as shut is a colleague reading that they earned no pool.
 */
export function gateSummary(statement: StatementGate): string {
  const percent = gateOpenPercent(statement.gateFactor);
  if (percent === null) return 'Gate not known';
  if (percent === '100%') return 'Gate fully open';
  if (percent === '0%') return 'Gate closed — the regional pool pays nothing';
  return `Gate ${percent} open`;
}

/* -------------------------------------------------------------------------
 * The payout, as rows.
 * --------------------------------------------------------------------- */

/**
 * What a row in the money block IS, so a caller can style the arithmetic rather
 * than guess at it from the label.
 *
 * ```
 * 'figure'     an input to the sum — the personal commission, the regional pool
 * 'gate'       the pool after the gate factor was applied
 * 'model'      the model's max, and therefore the gross payable
 * 'deduction'  subtracted from the gross
 * 'net'        the result
 * 'note'       ⛔ NOT part of the sum — see below
 * ```
 *
 * ⛔ **`'note'` IS THE ONE KIND THAT MUST NOT BE ADDED OR SUBTRACTED BY
 * ANYTHING.** It carries a figure that explains the result rather than
 * contributing to it, and a caller that styles it like a `'deduction'` invites a
 * reader to subtract it a second time — from a net that was already floored at
 * zero by it.
 */
/**
 * ⚠️ `'addition'` (migration 235) is the refund row — commission HANDED BACK,
 * which the server adds to the gross before the clawbacks are subtracted. It
 * is part of the sum, like a deduction, in the other direction.
 */
export type PayoutRowKind =
  | 'figure'
  | 'gate'
  | 'model'
  | 'addition'
  | 'deduction'
  | 'net'
  | 'note';

/**
 * One line of a statement's money block.
 *
 * ⛔ **`value` IS THE EXACT DECIMAL STRING, UNTOUCHED — never a formatted one and
 * never a number.** This module has no currency: the code lives on the statement,
 * and the three surfaces that render these rows legitimately format money
 * differently (a page in full, a dense row compacted). Formatting here would
 * either pick one for all of them or need a currency argument this module has no
 * business holding. **The caller formats, with the statement's own `currency`.**
 *
 * ⛔ **And the caller must never `Number()` it on the way.** See
 * {@link formatPercent} for the rule and what it costs.
 */
export interface PayoutRow {
  label: string;
  /** The exact decimal string the server sent. Format it; never parse it. */
  value: string;
  kind: PayoutRowKind;
}

/** What {@link payoutBreakdown} needs. Every money field is an exact decimal string. */
export interface CommissionPayoutFigures {
  /**
   * ⛔ **THE FLAG, WHICH IS THE ONLY THING THAT SEPARATES THE TWO MODELS.** Never
   * the model's slug and never its name — see {@link modelExplanation}.
   */
  netsPersonalFromPool: boolean;
  /** `DECIMAL(9,8)` as an exact string. */
  gateFactor: string;
  personalCommission: string;
  regionalPool: string;
  effectiveRegionalPool: string;
  grossPayable: string;
  /**
   * What a PREVIOUS approved run could not recover, subtracted alongside
   * `clawbackAmount` rather than inside it. ⛔ **Never add the two together** —
   * `clawbackAmount` is the sum of the `clawbacks` ledger and of nothing else, so
   * a combined figure is a subtraction that ledger cannot account for.
   */
  clawbackBroughtForward: string;
  /** The sum of the `clawbacks` ledger rows, and of no other query. */
  clawbackAmount: string;
  /**
   * Commission HANDED BACK — the sum of the statement's `refunds` rows (migration
   * 235): an unpaid-invoice clawback from an earlier approved run whose invoice
   * has since been paid. ADDED to the gross before the clawbacks come off it.
   *
   * ⚠️ Required on the type, tolerated missing at the border — see
   * {@link hasRefund}, the same deploy-order guard `hasBroughtForward` carries.
   */
  clawbackRefunded: string;
  /**
   * Everything still outstanding after this year — this year's own excess PLUS
   * whatever arrived brought forward and still could not be absorbed. Cumulative,
   * so it is never summed across years.
   */
  clawbackUnrecovered: string;
  netPayable: string;
  /**
   * New business (owner 2026-10-28, migration 335). `modelPayable` is the old
   * max() gross; `grossPayable` = `modelPayable` + `newBusinessCommission`.
   * Optional: `undefined` means an older API (or a statement predating 335), and
   * the money block then renders exactly as before.
   */
  modelPayable?: string;
  newBusinessCommission?: string;
  /** `DECIMAL(7,4)` as an exact string. */
  newBusinessRatePercent?: string;
}

/** Column and fact headings for the new business figures on statements and runs. */
export const NEW_BUSINESS_HEADINGS = {
  rate: 'New business rate',
  previousWindow: 'Previous window',
  accountsThisPeriod: 'Accounts this period',
  accountsLastPeriod: 'Same accounts last period',
  newBusiness: 'New business',
  newBusinessCommission: 'New business commission',
  grossPayable: 'Gross payable',
  accounts: 'New business by account',
  account: 'Account',
  thisPeriod: 'This period',
  lastPeriod: 'Last period',
  growth: 'Growth',
  share: 'Share %',
  newBusinessRecovered: 'New business recovered',
} as const;

/** The new business gate basis choices on a scheme (owner 2026-10-28). */
export const NEW_BUSINESS_GATE_BASIS_OPTIONS: ReadonlyArray<{
  value: NewBusinessGateBasis;
  label: string;
}> = [
  { value: 'scheme', label: 'Same as the scheme' },
  { value: 'none', label: 'No gate' },
  { value: 'personal', label: 'Personal target' },
  { value: 'regional', label: 'Regional target' },
  { value: 'both', label: 'Both targets' },
];

/** The label for a basis; `undefined`/`null` (older API or older statement) renders `—`. */
export function newBusinessGateBasisLabel(basis: NewBusinessGateBasis | null | undefined): string {
  if (basis === null || basis === undefined) return '—';
  return NEW_BUSINESS_GATE_BASIS_OPTIONS.find((option) => option.value === basis)?.label ?? basis;
}

/** Whether the statement carries a new business commission above zero (absent = older API = none). */
export function hasNewBusinessCommission(statement: CommissionPayoutFigures): boolean {
  const value: string | undefined = statement.newBusinessCommission;
  if (typeof value !== 'string') return false;
  return isPositiveAmount(value);
}

/**
 * Whether an exact decimal string carries anything above zero.
 *
 * ⚠️ **String surgery rather than `Number()`, on money, deliberately.** This is a
 * PRESENCE test, so there is no arithmetic to be inexact about — and writing it
 * with a parse would put a float conversion on a money field in the one module
 * that tells everybody else not to. `'0.00'` is false, `'0.01'` is true, and a
 * negative is false because a negative here is a defect rather than a figure.
 */
function isPositiveAmount(value: string): boolean {
  const text = value.trim();
  if (text === '' || text.startsWith('-')) return false;
  return /[1-9]/.test(text);
}

/**
 * THE ORDERED ROWS A STATEMENT'S MONEY BLOCK RENDERS.
 *
 * ```
 * Personal commission                                     figure
 * Regional pool                                           figure
 * Regional pool after the gate (43%)                      gate
 * <the model's own sentence>                              model
 * Clawback                                                deduction
 * Net payable                                             net
 * Clawback this year could not absorb                     note      only when > 0
 * ```
 *
 * ## ⛔ THE OFFSET SUBTRACTION AND THE TOP-UP TOP-UP READ DIFFERENTLY
 *
 * The models differ in **exactly one respect** — whether the personal commission
 * is netted out of the pool — so a shared neutral label such as *"Gross payable"*
 * would hide the only difference between them. Two statements on two models, on
 * one screen, would show the same six words over two different sums and the
 * reader would have no way to tell which rule produced either figure.
 *
 * ```
 * top-up   Greater of the personal commission and the pool
 * offset   Greater of the personal commission and the pool less that commission
 * ```
 *
 * ⚠️ **Both labels name the MAX, because the floor at the personal commission is
 * real on both models** and is the thing most likely to be read as a bug: an
 * Offset statement whose pool is smaller than the personal commission pays the
 * personal commission, not the difference, and not nothing.
 *
 * ## ⚠️ THE CLAWBACK ROW RENDERS AT ZERO AND THE UNRECOVERED ROW DOES NOT
 *
 * That looks inconsistent and is the whole design:
 *
 * - **The clawback is IN the sum**, so it renders always. A row that vanished at
 *   zero would make two statements show a different number of lines, and a reader
 *   comparing them could not tell *"nothing was clawed back"* from *"this screen
 *   does not show clawbacks"*. A reconciliation has to reconcile visibly.
 * - **The unrecovered remainder is NOT in the sum.** It is what the floor at zero
 *   left behind, so it renders only when there is something to say — the same
 *   rule {@link EXCLUDED_DOCUMENTS_NOTE} keeps, and for the same reason: a line
 *   reading zero on every statement is what teaches somebody to stop reading the
 *   lines.
 *
 * 🔴 **THE REMAINDER CARRIES INTO THE NEXT YEAR — owner's decision 2026-09-19,
 * migration 227.** ⚠️ *SUPERSEDED, and it was the reason this row was worded the
 * way it was: "whether it carries into the next year is a real decision **nobody
 * has taken**. ⛔ So do not word this row as **carried forward**".* It carries,
 * and a statement on the receiving end now shows it as its own deduction — see
 * {@link broughtForwardRows}. **The floor at zero is about not paying a negative
 * amount, never about writing the excess off.**
 *
 * ⛔ **THE OTHER HALF OF THAT INSTRUCTION SURVIVES: do not word any of this as
 * *"owed"*.** A debt a colleague has to the company is what owner decision 23
 * exists to make inexpressible, and that is untouched by the carry.
 *
 * ⚠️ **No row is ever synthesised.** Every value is a column the run computed and
 * froze; this function orders and labels them. If a figure looks wrong, it is
 * wrong on the statement, and the fix is a recompute rather than an adjustment
 * here.
 */
export function payoutBreakdown(statement: CommissionPayoutFigures): PayoutRow[] {
  const percent = gateOpenPercent(statement.gateFactor);

  const rows: PayoutRow[] = [
    {
      label: 'Personal commission',
      value: statement.personalCommission,
      kind: 'figure',
    },
    {
      label: 'Regional pool',
      value: statement.regionalPool,
      kind: 'figure',
    },
    {
      /*
       * The percentage rides in the label rather than being rendered as a second
       * figure: the row's VALUE is money, and a percentage in a money column is
       * the kind of thing a reader adds to the column above it.
       *
       * It comes from the same `gateOpenPercent` that `gateSummary` uses, so the
       * page header and this row cannot disagree about how far the gate opened —
       * which they would, by a whole percentage point, the first time somebody
       * rounded one of them independently.
       */
      label:
        percent === null
          ? 'Regional pool after the gate'
          : `Regional pool after the gate (${percent})`,
      value: statement.effectiveRegionalPool,
      kind: 'gate',
    },
    {
      label: statement.netsPersonalFromPool
        ? 'Greater of the personal commission and the pool less that commission'
        : 'Greater of the personal commission and the pool',
      /*
       * New business (migration 335): the model row is the max() alone, then
       * the new business commission is added and Gross payable shown. Without
       * the keys (older API / older statement) this is `grossPayable` and the
       * two extra rows never appear, so the block reads as before.
       */
      value:
        typeof statement.modelPayable === 'string' ? statement.modelPayable : statement.grossPayable,
      kind: 'model',
    },
    ...newBusinessRows(statement),
    /*
     * The refund comes BEFORE the deductions because the server adds it to the
     * gross before it subtracts them — read top to bottom, the column then does
     * exactly the arithmetic that produced Net payable. Only when positive: a
     * zero refund is the ordinary state and a row reading 0.00 on every
     * statement is the row that teaches somebody to stop reading rows.
     */
    ...refundedRows(statement),
    {
      label: 'Clawback',
      value: statement.clawbackAmount,
      kind: 'deduction',
    },
    /*
     * ⛔ A SECOND DEDUCTION ROW, AND NEVER FOLDED INTO THE ONE ABOVE.
     *
     * `clawbackAmount` is the sum of the `clawbacks` ledger and of nothing else,
     * so a combined figure is a subtraction that ledger cannot account for —
     * which is the one thing the ledger exists to do.
     *
     * ⚠️ **IT RENDERS ONLY WHEN POSITIVE, AND THAT IS THE HONEST ARM RATHER THAN
     * TIDINESS.** Zero means EITHER that the previous year recovered everything
     * OR that there is no previous approved year at all; nothing on the wire
     * separates them, so a zero row would have to state one of the two.
     */
    ...broughtForwardRows(statement),
    {
      label: 'Net payable',
      value: statement.netPayable,
      kind: 'net',
    },
  ];

  if (isPositiveAmount(statement.clawbackUnrecovered)) {
    rows.push({
      label: 'Clawback this year could not absorb',
      value: statement.clawbackUnrecovered,
      kind: 'note',
    });
  }

  return rows;
}

/**
 * THE ONE COPY OF THE BROUGHT-FORWARD WORDING.
 *
 * ⚠️ **THREE SURFACES RENDER A STATEMENT'S MONEY BLOCK** — `StatementPanel`, and
 * the card list and the table on `CommissionRunsPage`, which each lay it out
 * themselves rather than through {@link payoutBreakdown}. Three literals would
 * agree the day they were written and drift on the first correction, **and what
 * would drift is a sentence about money taken out of somebody's pay.**
 *
 * 🔴 **IT NAMES NO YEAR, AND THAT IS A LIMIT OF THE WIRE RATHER THAN A CHOICE OF
 * WORDS.** Measured 2026-09-19: the statement carries no source run and no prior
 * period — the only period fields on it are its own — so there is nothing to name
 * a year FROM. ⛔ **Do not derive one** by assuming the previous run, the previous
 * period or the year before this one: a statement is produced per assignment, an
 * assignment can start mid-history, and a guessed year on a payslip is a
 * confident wrong answer about somebody's pay. **Naming it is a backend field.**
 */
export const CLAWBACK_BROUGHT_FORWARD_LABEL = 'Clawback brought forward from an earlier year';

/**
 * The same fact where a cell has no room for the full label.
 *
 * ⛔ **DO NOT SHORTEN IT TO "brought forward", however tempting the 180px numeric
 * column makes that.** It shares a cell with the unrecovered remainder, and the
 * two point in OPPOSITE DIRECTIONS IN TIME — this one arrived from a previous
 * year, that one leaves for a later one. To an accountant *brought* and *carried*
 * forward settle that; to everybody else they are the same phrase, and the
 * shortened form is exactly as readable as the wrong one. **"from an earlier
 * year" is what makes the pair legible**, and it is worth the wrap.
 */
export const CLAWBACK_BROUGHT_FORWARD_SHORT = 'brought forward from an earlier year';

/**
 * Whether there is a brought-forward figure to show — **the ONE place the test
 * lives**, for the same reason the label is: three surfaces ask it.
 *
 * 🔴 **IT TOLERATES A MISSING VALUE, AND THAT IS A DEPLOY-ORDER GUARD RATHER THAN
 * DEFENSIVENESS.** This app ships separately from the API and can be NEWER than
 * it. Against a server predating migration 227 the key is **absent**, and every
 * presence test in this codebase reaches for `.trim()` — which throws on
 * `undefined` **inside a render**, and React escalates that to unmounting the
 * tree. The cost of the unguarded version is not a missing row: it is a blank
 * commission runs page.
 *
 * ⚠️ **Folding the absence to "nothing to show" is the HONEST answer, not a
 * convenient one.** A server that does not carry a remainder forward has not
 * carried one, so there is genuinely no deduction to render — the degradation is
 * exactly the behaviour that shipped before 227.
 *
 * ⛔ **This is why the field stays REQUIRED on the type rather than optional.** An
 * optional member would declare a third state the data cannot hold and invite a
 * `?? '0.00'` at every call site; the fold belongs HERE, once, at the border.
 */
export function hasBroughtForward(statement: CommissionPayoutFigures): boolean {
  const value: string | undefined = statement.clawbackBroughtForward;
  if (typeof value !== 'string') return false;
  return isPositiveAmount(value);
}

/**
 * The brought-forward deduction, as nought or one row.
 *
 * ⚠️ **Exported for the two surfaces that do not use {@link payoutBreakdown}**, so
 * the presence test and the wording cannot differ between the three.
 */
export function broughtForwardRows(statement: CommissionPayoutFigures): PayoutRow[] {
  if (!hasBroughtForward(statement)) return [];
  return [
    {
      label: CLAWBACK_BROUGHT_FORWARD_LABEL,
      value: statement.clawbackBroughtForward,
      kind: 'deduction',
    },
  ];
}

/**
 * THE ONE COPY OF THE REFUND WORDING — migration 235, owner decision 2026-09-24.
 *
 * A clawback taken because an invoice went unpaid is handed back on a later run
 * once the invoice is paid. Three surfaces render it, like the brought-forward
 * figure, so the wording lives here once.
 *
 * ⚠️ "since paid" is the fact that makes it a refund rather than a bonus. A bare
 * "refund" beside a commission figure reads as money given for no reason.
 */
export const CLAWBACK_REFUNDED_LABEL = 'Clawback refunded — invoice since paid';

/** The same fact in a cell that has no room for the full label. */
export const CLAWBACK_REFUNDED_SHORT = 'refunded — invoice since paid';

/**
 * Whether there is a refund to show. Tolerates a missing value — against a
 * server predating migration 235 the key is ABSENT, and `.trim()` on
 * `undefined` inside a render blanks the whole page. A server that does not
 * refund has refunded nothing, so folding the absence to "nothing to show" is
 * the honest answer.
 */
export function hasRefund(statement: CommissionPayoutFigures): boolean {
  const value: string | undefined = statement.clawbackRefunded;
  if (typeof value !== 'string') return false;
  return isPositiveAmount(value);
}

/**
 * The new business addition and the Gross payable it produces, as nought or two
 * rows: only when the new business commission is positive.
 */
export function newBusinessRows(statement: CommissionPayoutFigures): PayoutRow[] {
  if (!hasNewBusinessCommission(statement) || statement.newBusinessCommission === undefined) {
    return [];
  }
  return [
    {
      label: NEW_BUSINESS_HEADINGS.newBusinessCommission,
      value: statement.newBusinessCommission,
      kind: 'addition',
    },
    {
      label: NEW_BUSINESS_HEADINGS.grossPayable,
      value: statement.grossPayable,
      kind: 'figure',
    },
  ];
}

/** The refund addition, as nought or one row. */
export function refundedRows(statement: CommissionPayoutFigures): PayoutRow[] {
  if (!hasRefund(statement)) return [];
  return [
    {
      label: CLAWBACK_REFUNDED_LABEL,
      value: statement.clawbackRefunded,
      kind: 'addition',
    },
  ];
}

/**
 * WHO A CLAWBACK WAS CHARGED TO, for the ledger row — owner decision 2026-09-24.
 *
 * Since that decision a clawback is charged to the lead who was PAID the
 * commission on the revenue, read off the approved statement that counted it.
 * `sourceUserId` names that person; where it is `null`, no approved statement
 * ever counted the document — commission started after it was billed — and the
 * server fell back to the document's CURRENT account lead. That fallback is the
 * one case a reader should be told about, so it is the one this words.
 *
 * ⚠️ ONLY ON A CREDIT-NOTE OR UNPAID-INVOICE ROW. Since the second owner
 * decision of 2026-09-24 the VOID arm has no fallback — an invoice nobody was
 * paid on is not clawed at all — so a void row always carries `sourceUserId`.
 *
 * ⚠️ `null` is returned when there is nothing to say — the ordinary case, where
 * the lead charged is the lead who was paid.
 */
export function clawbackAttributionNote(sourceUserId: number | null): string | null {
  return sourceUserId === null
    ? 'No approved statement counted this — charged to the current account lead'
    : null;
}

/**
 * THE STRANDED-REMAINDER SENTENCE — owner decision 2026-09-24.
 *
 * A lead with a clawback remainder on their latest approved statement and no
 * assignment in this run's period: the remainder keeps carrying, but this run
 * has nowhere to land it. Worded as a fact about the RUN rather than as a debt
 * — ⛔ never "owed", which owner decision 23 exists to make inexpressible.
 */
export function strandedRemainderSentence(periodName: string): string {
  return `still to recover from ${periodName} — no assignment in this run, so nothing is recovered here`;
}

/**
 * The clawback kinds as a person reads them. Moved here from `StatementPanel`
 * on 2026-09-24 so the statement ledger and the runs page word them once.
 *
 * ⚠️ MATCHING ON THIS SLUG IS SAFE AND THAT IS WORTH SAYING, because this
 * codebase forbids matching on a master's NAME in four other places. `kind` is
 * bounded by `ck_f_commission_statement_clawbacks_kind` — a column CHECK over
 * three literals decided by owner decision 10 — and is NOT a row an
 * administrator can add, rename or retire. An unknown spelling still falls
 * through to itself rather than to a heading nobody chose.
 */
export const CLAWBACK_KIND_LABELS: Record<string, string> = {
  credit_note: 'Credit note',
  void_invoice: 'Voided invoice',
  unpaid_invoice: 'Unpaid invoice',
};

/**
 * THE HEADING FOR A PAID LEAD WITH NO ASSIGNMENT THIS PERIOD — owner decision
 * 2026-09-24. Their clawbacks are found but cannot be deducted, because there
 * is no statement this run to deduct them from.
 *
 * ⛔ THE AMOUNTS UNDER IT ARE THE DOCUMENTS' OWN MONEY — revenue given back or
 *    written off — and NOT commission. Worded so, because the commission figure
 *    depends on a rate and a gate no run has computed for this lead this year.
 *    ⛔ Never "owed", for the same reason `strandedRemainderSentence` avoids it.
 */
export const UNASSIGNED_CLAWBACKS_HEADING =
  'Clawbacks on revenue a lead was paid on, with no assignment in this run — found, not deducted';

export const UNASSIGNED_CLAWBACKS_AMOUNT_NOTE =
  'Document totals in their own currencies, not commission';

/* -------------------------------------------------------------------------
 * The model, explained.
 * --------------------------------------------------------------------- */

/** What {@link modelExplanation} is handed. */
export interface CommissionModelFlags {
  /**
   * ⚠️ **ACCEPTED AND DELIBERATELY UNREAD.** It is in the signature because a
   * caller already holds the model object and unpacking it at the call site is
   * the shape that lets somebody pass the wrong flag with the right name — and
   * because a reader who greps for `slug` in this file should find this note
   * rather than conclude the slug was unavailable and go and add it.
   *
   * ⛔ **A later pass must not "use" it.** See the function.
   */
  slug: string;
  /** The only thing this function reads. */
  netsPersonalFromPool: boolean;
}

/**
 * WHAT THIS MODEL PAYS, in a sentence.
 *
 * ## ⛔ IT BRANCHES ON `netsPersonalFromPool`, NEVER ON THE SLUG AND NEVER ON THE
 *    NAME
 *
 * **This codebase has been bitten four times by a client deciding behaviour from
 * an admin-editable string**, and every one of them was correct on the day it
 * shipped:
 *
 * ```
 * an invoice status  includes('paid')     read "Unpaid" as settled
 * a quote stage      includes('accept')   painted "Not Accepted" green
 * a product name     /master ?class/i     an ordinary rename switched inclusions off
 * an inclusion name  a fuzzy match        matched the wrong master row
 * ```
 *
 * A model's `name` is admin-adjacent text and its `slug` is a join key, not a
 * rule. The flag is the machine-readable fact, it is the **only** thing the
 * server's own arithmetic reads, and a migration guard asserts that the seeded
 * models **disagree** on it — because two models both carrying the same value
 * satisfies every row count, seeds a catalogue that looks complete, and makes one
 * model compute as the other **every year, on a figure somebody is paid
 * against**, with nothing on any screen to say so.
 *
 * ⚠️ **SO A FURTHER MODEL SHARING A FLAG VALUE GETS THE SAME SENTENCE, AND THAT IS
 * CORRECT RATHER THAN A GAP.** The flag IS the difference; a model that differs
 * in some other respect needs a second column and a deliberate decision, and it
 * is not a row somebody adds through the app — the masters are closed to writes
 * for exactly this reason. ⛔ **Do not "handle" a new model by adding a slug
 * branch here**: that would put a client-side rule over a server-side formula
 * that has not changed, so the sentence and the money would describe different
 * things.
 *
 * ⚠️ **The sentence describes what the model pays, not what THIS statement paid.**
 * It belongs on a scheme form and at the head of a statement's money block;
 * {@link payoutBreakdown} is what says which arm of the max actually won.
 */
export function modelExplanation(model: CommissionModelFlags): string {
  return model.netsPersonalFromPool
    ? 'The regional pool is paid less the personal commission already earned, and never ' +
        'less than that commission on its own.'
    : 'The regional pool tops the lead up to their share of it, and never pays less than ' +
        'the personal commission on its own.';
}
